import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import Hls from 'hls.js'
import {
  applyCloudFrontCookies,
  type CloudFrontCredentials
} from 'helpers/cloudfrontCookies'
import { MostPlayedTracker } from 'features/VideoPlayer/mostPlayedTracker'
import { useUserProfile } from 'store'
import { useQueryClient } from 'react-query'

export type GlobalVideoOwnerType = 'post' | 'preview' | 'message' | 'story'

export interface GlobalVideoOwner {
  type: GlobalVideoOwnerType
  id: string
}

export interface GlobalVideoMedia {
  id: string
  url: string
  poster?: string
  videoId?: string
  videoUserId?: string
  cloudfront?: CloudFrontCredentials | null
}

export interface AttachVideoOptions {
  owner: GlobalVideoOwner
  host: HTMLElement
  media: GlobalVideoMedia
  isFeedHost?: boolean
}

export interface GlobalVideoContextValue {
  activeMediaId: string | null
  setActiveMediaId: (mediaId: string | null) => void

  prepare: (options: AttachVideoOptions) => void
  attach: (options: AttachVideoOptions) => void
  detach: (owner: GlobalVideoOwner, host?: HTMLElement | null) => void

  play: (mediaId?: string, url?: string) => Promise<void>
  pause: () => void
  seekTo: (value: number, type?: 'seconds' | 'fraction') => void
  stopLoading: () => void

  setMuted: (muted: boolean) => void
  setVolume: (volume: number) => void
  setPlaybackRate: (rate: number) => void
  setQualityLevel: (level: number) => void

  getVideoElement: (mediaId?: string, url?: string) => HTMLVideoElement | null
  getHls: () => Hls | null
  getCurrentMedia: () => GlobalVideoMedia | null
  getCurrentOwner: () => GlobalVideoOwner | null
  getCurrentTime: () => number
  getDuration: () => number
  getSelectedQualityLevel: () => number

  getMostPlayedSegments: () => number[]
  isCurrentMedia: (mediaId: string) => boolean
}

interface PlayerState {
  key: string
  media: GlobalVideoMedia
  video: HTMLVideoElement
  hls: Hls | null
  owner: GlobalVideoOwner | null
  host: HTMLElement | null
  feedHost: HTMLElement | null
  sourceReady: boolean
  selectedQuality: number
  currentQuality: number
  qualityHoldTimer: number | null
  mostPlayedTracker: MostPlayedTracker
}

const GlobalVideoContext =
  createContext<GlobalVideoContextValue | undefined>(undefined)

interface Props {
  children: ReactNode
}

const ownerMatches = (
  a: GlobalVideoOwner | null,
  b: GlobalVideoOwner
) =>
  !!a &&
  a.type === b.type &&
  a.id === b.id

const getPlayerKey = (media: GlobalVideoMedia) =>
  `${String(media.id)}::${media.url.split('?')[0]}`

export const GlobalVideoProvider: FC<Props> = ({ children }) => {
  /*
   * activeMediaId is still the globally selected feed media.
   *
   * The difference from the old implementation is that each media now keeps
   * its own persistent <video> + HLS instance. Only one player is allowed to
   * actively play/load at a time, but switching media no longer destroys the
   * previous player's element, frame, currentTime or buffer.
   *
   * Feed -> fullscreen for the SAME media still moves the exact same video
   * element between hosts with appendChild(), so playback/audio is continuous.
   */
  const [activeMediaId, setActiveMediaId] = useState<string | null>(null)
  const playersRef = useRef<Map<string, PlayerState>>(new Map())
  const currentPlayerKeyRef = useRef<string | null>(null)
  const userData = useUserProfile()
  const queryClient = useQueryClient()

  const credentialRefreshRef = useRef<
    Map<string, number>
  >(new Map())

  const credentialRefreshPromiseRef =
    useRef<Promise<void> | null>(null)

  const refreshActiveMediaQueries =
    useCallback((): Promise<void> => {
      if (credentialRefreshPromiseRef.current) {
        return credentialRefreshPromiseRef.current
      }

      const promise = queryClient.refetchQueries({
        active: true
      }).then(() => undefined)

      credentialRefreshPromiseRef.current = promise

      void promise.then(
        () => {
          credentialRefreshPromiseRef.current = null
        },
        () => {
          credentialRefreshPromiseRef.current = null
        }
      )

      return promise
    }, [queryClient])

  const refreshMediaCredentials = useCallback(
    (media: GlobalVideoMedia) => {
      const key = String(
        media.videoId ?? media.id
      )

      const now = Date.now()
      const lastAttempt =
        credentialRefreshRef.current.get(key) ?? 0

      // Prevent repeated requests from the same
      // video while recovery is already underway.
      if (now - lastAttempt < 30_000) {
        return
      }

      credentialRefreshRef.current.set(key, now)

      console.warn(
        '[CloudFront] Refreshing active queries',
        { videoId: media.videoId }
      )

      void refreshActiveMediaQueries().catch(error => {
        console.warn(
          '[CloudFront] Credential refresh failed',
          error
        )
      })
    },
    [refreshActiveMediaQueries]
  )

/**
 * Refresh expired CloudFront credentials when
 * the user returns to the browser.
 *
 * The owning React Query feeds receive fresh data.
 * VideoSurface then passes the new credentials
 * to the existing persistent players.
 */
useEffect(() => {
  const refreshOnResume = () => {
    if (document.visibilityState !== 'visible') {
      return
    }

    const now = Math.floor(Date.now() / 1000)
    const refreshBufferSeconds = 60

    const hasExpiringVideo = Array.from(
      playersRef.current.values()
    ).some(player => {
      const credentials = player.media.cloudfront

      return (
        !!credentials &&
        credentials.expires_at <=
          now + refreshBufferSeconds
      )
    })

    if (!hasExpiringVideo) {
      return
    }

    console.log(
      '[CloudFront] Browser resumed, refreshing expired media'
    )

    void refreshActiveMediaQueries().catch(error => {
      console.warn(
        '[CloudFront] Resume refresh failed',
        error
      )
    })
  }

  document.addEventListener(
    'visibilitychange',
    refreshOnResume
  )

  window.addEventListener(
    'focus',
    refreshOnResume
  )

  window.addEventListener(
    'pageshow',
    refreshOnResume
  )

  return () => {
    document.removeEventListener(
      'visibilitychange',
      refreshOnResume
    )

    window.removeEventListener(
      'focus',
      refreshOnResume
    )

    window.removeEventListener(
      'pageshow',
      refreshOnResume
    )
  }
}, [refreshActiveMediaQueries])


  const destroyPlayerHls = useCallback((player: PlayerState) => {
    if (player.qualityHoldTimer !== null) {
      window.clearTimeout(player.qualityHoldTimer)
      player.qualityHoldTimer = null
    }

    if (!player.hls) return

    try {
      player.hls.destroy()
    } catch (error) {
      console.warn('[GlobalVideo] Failed to destroy HLS', error)
    }

    player.hls = null
    player.selectedQuality = -1
    player.currentQuality = -1
    player.sourceReady = false
  }, [])

  const destroyPlayer = useCallback(
    (player: PlayerState) => {
      player.video.pause()
      destroyPlayerHls(player)
      player.mostPlayedTracker.destroy()

      player.video.removeAttribute('src')

      try {
        player.video.load()
      } catch {
        // Ignore teardown errors.
      }

      player.video.remove()
      playersRef.current.delete(player.key)

      if (currentPlayerKeyRef.current === player.key) {
        currentPlayerKeyRef.current = null
      }
    },
    [destroyPlayerHls]
  )

  const pruneDisconnectedPlayers = useCallback(() => {
    for (const player of Array.from(playersRef.current.values())) {
      if (player.owner) continue
      if (player.video.isConnected) continue
      if (currentPlayerKeyRef.current === player.key) continue
      destroyPlayer(player)
    }
  }, [destroyPlayer])

  const syncMostPlayedSession = useCallback(
    (player: PlayerState, media: GlobalVideoMedia) => {
      const isBlob =
        media.url.startsWith('blob:') ||
        media.url.startsWith('data:video')

      const isOwner = Boolean(
        media.videoUserId &&
        userData?.id &&
        String(media.videoUserId) === String(userData.id)
      )

      player.mostPlayedTracker.setSession({
        videoId: media.videoId,
        enabled:
          Boolean(media.videoId) &&
          !isBlob &&
          !isOwner
      })
    },
    [userData?.id]
  )

  const loadPlayerSource = useCallback(
    (player: PlayerState) => {
      if (player.sourceReady) return

      const { media, video } = player

      const isProtectedHls =
        media.url.startsWith('https://video-cdn.allfans.com/') &&
        media.url.split('?')[0].endsWith('.m3u8')

      if (isProtectedHls) {
        if (
          !media.cloudfront ||
          !applyCloudFrontCookies(media.cloudfront)
        ) {
          refreshMediaCredentials(media)
          return
        }
      }

      if (media.poster) {
        video.poster = media.poster
      } else {
        video.removeAttribute('poster')
      }

      syncMostPlayedSession(player, media)

      const cleanUrl = media.url.split('?')[0].toLowerCase()
      const isHls = cleanUrl.endsWith('.m3u8')

      if (isHls && Hls.isSupported()) {
        const isProdCdn = media.url.startsWith(
          'https://video-cdn.allfans.com'
        )

        const hls = new Hls({
          capLevelToPlayerSize: false,
          // Prepare manifests immediately, but do not continuously fetch
          // fragments for every inactive feed video.
          autoStartLoad: false,
          xhrSetup: xhr => {
            xhr.withCredentials = isProdCdn
          }
        })

        player.hls = hls
        player.sourceReady = true

        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          const levelsAtOrBelow1080 = data.levels
            .map((level, index) => ({
              index,
              height: level.height ?? 0
            }))
            .filter(level =>
              level.height > 0 &&
              level.height <= 1080
            )

          const defaultLevel =
            levelsAtOrBelow1080.length > 0
              ? levelsAtOrBelow1080.reduce(
                  (best, level) =>
                    level.height > best.height
                      ? level
                      : best
                ).index
              : data.levels.length - 1

          hls.currentLevel = defaultLevel
          hls.nextLevel = defaultLevel
          hls.loadLevel = defaultLevel

          player.selectedQuality = defaultLevel
          player.currentQuality = defaultLevel
        })

        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          player.currentQuality = data.level
        })

        hls.on(Hls.Events.ERROR, (_, data) => {
          const status =
            (data.response as any)?.code ??
            (data.response as any)?.status ??
            (data.networkDetails as any)?.status

          if (status === 401 || status === 403) {
            console.warn(
              '[CloudFront] Authorization rejected',
              {
                videoId: player.media.videoId,
                status
              }
            )

            if (player.hls === hls) {
              destroyPlayerHls(player)
            }

            refreshMediaCredentials(player.media)
            return
          }

          if (!data.fatal) return

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.warn(
                '[GlobalVideo] HLS network error, retrying',
                data
              )
              hls.startLoad()
              break

            case Hls.ErrorTypes.MEDIA_ERROR:
              console.warn(
                '[GlobalVideo] HLS media error, recovering',
                data
              )
              hls.recoverMediaError()
              break

            default:
              console.error(
                '[GlobalVideo] Fatal HLS error',
                data
              )

              if (player.hls === hls) {
                destroyPlayerHls(player)
              }

              break
          }
        })

        hls.attachMedia(video)
        hls.loadSource(media.url)
        return
      }

      /* Native MP4/blob/data URLs, plus Safari native HLS. */
      video.src = media.url
      video.load()
      player.sourceReady = true
    },
    [
      destroyPlayerHls,
      syncMostPlayedSession,
      refreshMediaCredentials
    ]
  )

  const ensurePlayer = useCallback(
    (media: GlobalVideoMedia) => {
      const key = getPlayerKey(media)
      const existing = playersRef.current.get(key)

      if (existing) {
        const oldCredentials =
          existing.media.cloudfront

        const newCredentials =
          media.cloudfront

        const credentialsChanged =
          !!newCredentials &&
          (
            oldCredentials?.signature !==
              newCredentials.signature ||
            oldCredentials?.expires_at !==
              newCredentials.expires_at
          )

        if (credentialsChanged && newCredentials) {
          const oldCredentialsExpired =
            !!oldCredentials &&
            oldCredentials.expires_at <=
              Math.floor(Date.now() / 1000)

          const applied =
            applyCloudFrontCookies(newCredentials)

          if (applied && oldCredentialsExpired) {
            // The old HLS session may have failed
            // while the browser was inactive.
            // Reinitialize its source using fresh cookies.
            if (existing.hls) {
              destroyPlayerHls(existing)
            } else if (existing.sourceReady) {
              existing.video.pause()
              existing.video.removeAttribute('src')
              existing.video.load()
              existing.sourceReady = false
            }

            console.log(
              '[CloudFront] Restored expired video',
              { videoId: media.videoId }
            )
          }
        }

        existing.media = {
          ...existing.media,
          ...media,
          videoId:
            media.videoId ??
            existing.media.videoId,
          videoUserId:
            media.videoUserId ??
            existing.media.videoUserId,
          cloudfront:
            media.cloudfront ??
            existing.media.cloudfront
        }

        // Keep the native poster stable when reusing an existing player.
        // Fullscreen can display its own cached thumbnail overlay.
        if (!existing.video.poster && media.poster) {
          existing.video.poster = media.poster
        }

        syncMostPlayedSession(existing, existing.media)

        if (!existing.sourceReady) {
          loadPlayerSource(existing)
        }

        return existing
      }

      const video = document.createElement('video')

      video.className = 'global-video-element'
      video.playsInline = true
      video.controls = false
      video.preload = 'auto'
      video.style.display = 'block'
      video.style.background = 'transparent'
      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')

      const mostPlayedTracker = new MostPlayedTracker()
      mostPlayedTracker.bind(video)

      const player: PlayerState = {
        key,
        media,
        video,
        hls: null,
        owner: null,
        host: null,
        feedHost: null,
        sourceReady: false,
        selectedQuality: -1,
        currentQuality: -1,
        qualityHoldTimer: null,
        mostPlayedTracker
      }

      playersRef.current.set(key, player)
      loadPlayerSource(player)
      return player
    },
    [
      loadPlayerSource,
      syncMostPlayedSession,
      destroyPlayerHls
    ]
  )

  const getCurrentPlayer = useCallback(() => {
    const key = currentPlayerKeyRef.current
    return key ? playersRef.current.get(key) ?? null : null
  }, [])

  const pauseAndStopOtherPlayers = useCallback(
    (keepKey: string | null) => {
      for (const player of Array.from(playersRef.current.values())) {
        if (player.key === keepKey) continue

        if (!player.video.paused) {
          player.video.pause()
        }

        try {
          player.hls?.stopLoad()
        } catch {
          // Ignore HLS stop errors.
        }
      }
    },
    []
  )

  const holdCurrentQuality = useCallback((player: PlayerState) => {
    const hls = player.hls
    if (!hls) return

    // Fixed quality selected by user: leave it completely alone.
    if (player.selectedQuality !== -1) return

    const currentLevel = hls.currentLevel
    if (currentLevel < 0) return

    if (player.qualityHoldTimer !== null) {
      window.clearTimeout(player.qualityHoldTimer)
    }

    hls.currentLevel = currentLevel
    hls.nextLevel = currentLevel
    hls.loadLevel = currentLevel

    player.qualityHoldTimer = window.setTimeout(() => {
      if (player.hls === hls) {
        hls.currentLevel = -1
        hls.nextLevel = -1
        hls.loadLevel = -1
      }

      player.qualityHoldTimer = null
    }, 1500)
  }, [])

  /*
   * Prepare an inactive feed surface without making it the active owner.
   * This is what lets the next visible video have its own video/HLS instance
   * ready before the user presses play.
   *
   * prepare() never steals a connected video element from another surface.
   */
  const prepare = useCallback(
    ({ host, media, isFeedHost = false }: AttachVideoOptions) => {
      pruneDisconnectedPlayers()

      const player = ensurePlayer(media)

      if (isFeedHost) {
        player.feedHost = host
      }

      if (
        isFeedHost &&
        !player.owner &&
        player.video.parentElement !== host
      ) {
        host.appendChild(player.video)
        player.host = host
      } else if (!player.video.isConnected) {
        host.appendChild(player.video)
        player.host = host
      }
    },
    [ensurePlayer, pruneDisconnectedPlayers]
  )

  const attach = useCallback(
    ({ owner, host, media, isFeedHost = false }: AttachVideoOptions) => {
      pruneDisconnectedPlayers()

      const player = ensurePlayer(media)

      if (isFeedHost) {
        player.feedHost = host
      }

      const previousPlayer = getCurrentPlayer()
      const previousOwner = player.owner
      const sameMedia = previousPlayer?.key === player.key

      /*
       * Different media: pause/stop the previous player, but KEEP its
       * element, source, currentTime, frame and existing buffer intact.
       */
      if (previousPlayer && !sameMedia) {
        previousPlayer.video.pause()

        try {
          previousPlayer.hls?.stopLoad()
        } catch {
          // Ignore HLS stop errors.
        }
      }

      /*
       * Same media moving feed -> fullscreen: preserve the exact same video
       * element and HLS instance. No pause, no source change, no reload.
       */
      const enteringPreview =
        sameMedia &&
        previousOwner?.type === 'post' &&
        owner.type === 'preview'

      if (enteringPreview && player.selectedQuality === -1) {
        holdCurrentQuality(player)
      }

    player.media = {
      ...player.media,
      ...media,
      videoId:
        media.videoId ??
        player.media.videoId,
      videoUserId:
        media.videoUserId ??
        player.media.videoUserId,
      cloudfront:
        media.cloudfront ??
        player.media.cloudfront
    }

      syncMostPlayedSession(player, player.media)



      player.owner = owner
      player.host = host
      
      currentPlayerKeyRef.current = player.key

      if (player.video.parentElement !== host) {
        host.appendChild(player.video)
      }

    },
    [
      ensurePlayer,
      getCurrentPlayer,
      holdCurrentQuality,
      pruneDisconnectedPlayers,
      syncMostPlayedSession
    ]
  )

const detach = useCallback(
  (
    owner: GlobalVideoOwner,
    host?: HTMLElement | null
  ) => {
    const player = Array.from(
      playersRef.current.values()
    ).find(candidate => {
      if (!ownerMatches(candidate.owner, owner)) return false

      if (
        host &&
        candidate.host &&
        candidate.host !== host
      ) {
        return false
      }

      return true
    })

    if (!player) return

    /*
     * Do NOT pause/remove here.
     *
     * React may be transferring the same persistent player:
     * feed -> preview or preview -> feed.
     *
     * The video remains parked in its last host until another surface for
     * the same media actively attaches and moves that exact element.
     */
    if (
      player.feedHost?.isConnected &&
      player.video.parentElement !== player.feedHost
    ) {
      player.feedHost.appendChild(player.video)
      player.host = player.feedHost
    }

    player.owner = null

    window.setTimeout(() => {
      /*
       * If the host was actually unmounted rather than merely becoming
       * inactive, the element will no longer be connected. Prune it later
       * unless it became current again during a transfer.
       */
      if (
        !player.owner &&
        !player.video.isConnected &&
        currentPlayerKeyRef.current !== player.key
      ) {
        destroyPlayer(player)
      }
    }, 0)
  },
  [destroyPlayer]
)

  /*
   * activeMediaId still guarantees only one selected feed player.
   * Changing A -> B pauses/stops A immediately, without destroying it.
   */
  useEffect(() => {
    if (activeMediaId === null) {
      pauseAndStopOtherPlayers(null)
      return
    }

    for (const player of Array.from(playersRef.current.values())) {
      if (String(player.media.id) === String(activeMediaId)) {
        continue
      }

      player.video.pause()

      try {
        player.hls?.stopLoad()
      } catch {
        // Ignore HLS stop errors.
      }
    }
  }, [activeMediaId, pauseAndStopOtherPlayers])

  const stopLoading = useCallback(() => {
    const player = getCurrentPlayer()

    try {
      player?.hls?.stopLoad()
    } catch {
      // Ignore HLS stop errors.
    }
  }, [getCurrentPlayer])

  const play = useCallback(async (mediaId?: string, url?: string) => {
    let player: PlayerState | null = null

    if (mediaId && url) {
      const key = getPlayerKey({ id: mediaId, url })
      player = playersRef.current.get(key) ?? null
    } else if (mediaId) {
      player = Array.from(playersRef.current.values()).find(
        candidate => String(candidate.media.id) === String(mediaId)
      ) ?? null
    } else {
      player = getCurrentPlayer()
    }

  if (!player) return

  const isProtectedHls =
    player.media.url.startsWith('https://video-cdn.allfans.com/') &&
    player.media.url.split('?')[0].endsWith('.m3u8')

    if (
      isProtectedHls &&
      (
        !player.media.cloudfront ||
        !applyCloudFrontCookies(player.media.cloudfront)
      )
    ) {
      refreshMediaCredentials(player.media)
      return
    }

  currentPlayerKeyRef.current = player.key

    pauseAndStopOtherPlayers(player.key)

    if (!player.sourceReady) {
      loadPlayerSource(player)
    }

    try {
      player.hls?.startLoad()
    } catch {
      // Native video or HLS not ready yet.
    }

    try {
      await player.video.play()
    } catch {
      // Playback may be interrupted or blocked by the browser.
    }
  }, [
    getCurrentPlayer,
    loadPlayerSource,
    pauseAndStopOtherPlayers,
    refreshMediaCredentials
  ])

  const pause = useCallback(() => {
    const player = getCurrentPlayer()

    player?.video.pause()
  }, [getCurrentPlayer])

  const seekTo = useCallback(
    (
      value: number,
      type: 'seconds' | 'fraction' = 'seconds'
    ) => {
      const video = getCurrentPlayer()?.video

      if (
        !video ||
        !Number.isFinite(video.duration) ||
        video.duration <= 0
      ) {
        return
      }

      const rawTarget =
        type === 'fraction'
          ? value * video.duration
          : value

      video.currentTime = Math.max(
        0,
        Math.min(rawTarget, video.duration)
      )
    },
    [getCurrentPlayer]
  )

  const setMuted = useCallback(
    (muted: boolean) => {
      const player = getCurrentPlayer()
      if (!player) return
      player.video.muted = muted
    },
    [getCurrentPlayer]
  )

  const setVolume = useCallback(
    (volume: number) => {
      const player = getCurrentPlayer()
      if (!player) return

      player.video.volume =
        Math.max(0, Math.min(100, volume)) / 100
    },
    [getCurrentPlayer]
  )

  const setPlaybackRate = useCallback(
    (rate: number) => {
      const player = getCurrentPlayer()
      if (!player) return

      player.video.playbackRate =
        Math.max(0.25, Math.min(4, rate))
    },
    [getCurrentPlayer]
  )

  const setQualityLevel = useCallback(
    (height: number) => {
      const player = getCurrentPlayer()
      const hls = player?.hls

      if (!player || !hls) return

      if (height === -1) {
        hls.currentLevel = -1
        hls.nextLevel = -1
        hls.loadLevel = -1

        player.selectedQuality = -1
        player.currentQuality = -1
        return
      }

      const levelIndex = hls.levels.findIndex(
        level => level.height === height
      )

      if (levelIndex === -1) return

      hls.currentLevel = levelIndex
      hls.nextLevel = levelIndex
      hls.loadLevel = levelIndex

      player.selectedQuality = levelIndex
      player.currentQuality = levelIndex
    },
    [getCurrentPlayer]
  )

  const getVideoElement = useCallback(
    (mediaId?: string, url?: string) => {
      if (!mediaId) {
        return getCurrentPlayer()?.video ?? null
      }

      if (url) {
        const key = `${String(mediaId)}::${url.split('?')[0]}`
        return playersRef.current.get(key)?.video ?? null
      }

      const player = Array.from(playersRef.current.values()).find(
        candidate => String(candidate.media.id) === String(mediaId)
      )

      return player?.video ?? null
    },
    [getCurrentPlayer]
  )

  const getHls = useCallback(
    () => getCurrentPlayer()?.hls ?? null,
    [getCurrentPlayer]
  )

  const getCurrentMedia = useCallback(
    () => getCurrentPlayer()?.media ?? null,
    [getCurrentPlayer]
  )

  const getCurrentOwner = useCallback(
    () => getCurrentPlayer()?.owner ?? null,
    [getCurrentPlayer]
  )

  const getCurrentTime = useCallback(
    () => getCurrentPlayer()?.video.currentTime ?? 0,
    [getCurrentPlayer]
  )

  const getDuration = useCallback(() => {
    const duration = getCurrentPlayer()?.video.duration

    return Number.isFinite(duration) && (duration ?? 0) > 0
      ? duration!
      : 0
  }, [getCurrentPlayer])

  const getSelectedQualityLevel = useCallback(
    () => getCurrentPlayer()?.selectedQuality ?? -1,
    [getCurrentPlayer]
  )

  const getMostPlayedSegments = useCallback(
    () =>
      getCurrentPlayer()?.mostPlayedTracker.getSegments() ??
      Array(50).fill(0),
    [getCurrentPlayer]
  )

  const isCurrentMedia = useCallback(
    (mediaId: string) =>
      String(getCurrentPlayer()?.media.id ?? '') ===
      String(mediaId),
    [getCurrentPlayer]
  )

  /* Keep owner-sensitive MostPlayed sessions correct if the viewer changes. */
  useEffect(() => {
    for (const player of Array.from(playersRef.current.values())) {
      syncMostPlayedSession(player, player.media)
    }
  }, [syncMostPlayedSession])

  useEffect(() => {
    return () => {
      for (const player of Array.from(playersRef.current.values())) {
        destroyPlayer(player)
      }

      playersRef.current.clear()
      currentPlayerKeyRef.current = null
    }
  }, [destroyPlayer])

  const value = useMemo<GlobalVideoContextValue>(
    () => ({
      activeMediaId,
      setActiveMediaId,

      prepare,
      attach,
      detach,

      play,
      pause,
      stopLoading,
      seekTo,

      setMuted,
      setVolume,
      setPlaybackRate,
      setQualityLevel,

      getVideoElement,
      getHls,

      getCurrentMedia,
      getCurrentOwner,
      getCurrentTime,
      getDuration,
      getSelectedQualityLevel,
      getMostPlayedSegments,

      isCurrentMedia
    }),
    [
      activeMediaId,
      prepare,
      attach,
      detach,
      play,
      pause,
      stopLoading,
      seekTo,
      setMuted,
      setVolume,
      setPlaybackRate,
      setQualityLevel,
      getVideoElement,
      getHls,
      getCurrentMedia,
      getCurrentOwner,
      getCurrentTime,
      getDuration,
      getSelectedQualityLevel,
      getMostPlayedSegments,
      isCurrentMedia
    ]
  )

  return (
    <GlobalVideoContext.Provider value={value}>
      {children}
    </GlobalVideoContext.Provider>
  )
}

export const useGlobalVideo = () => {
  const context = useContext(GlobalVideoContext)

  if (!context) {
    throw new Error(
      'useGlobalVideo must be used inside GlobalVideoProvider'
    )
  }

  return context
}
